<template>
  <ts-modal
    ref="modal"
    modal-title="Edit candidate"
  >
    <template #modal-content>
      <loading-indicator
        v-if="isLoading"
        message="Please wait while action is performed ..."
        :vertical="true"
        additional-css-class="mb-4"
      />
      <div
        v-if="error"
        class="alert alert-danger"
        role="alert"
      >
        {{ error }}
      </div>
      <div
        v-if="showForm"
        v-html="formHTML"
      />
    </template>
    <template #modal-footer>
      <button
        type="button"
        class="btn btn-sm btn-secondary"
        data-bs-dismiss="modal"
      >
        Close
      </button>
      <button
        type="button"
        class="btn btn-sm btn-primary"
        :disabled="!canSubmit"
        @click="submitForm"
      >
        Save changes
      </button>
    </template>
  </ts-modal>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef } from "vue"
import $ from 'jquery'
import axios from 'axios'
import Rails from '@rails/ujs'
import TsModal from './ts-modal.vue'
import LoadingIndicator from './loading-indicator.vue'
import { isNonEmptyString } from '../js-utils'
import { initializeFormFields } from '../site-utils'

type FormResponse = {
  status?: string | null
  html?: string | null
}

const emit = defineEmits<{
  success: []
}>()

const modal = useTemplateRef<InstanceType<typeof TsModal>>("modal")

const isLoading = ref(false)
const error = ref<string | null>(null)
const formHTML = ref<string | null>(null)
const formEl = ref<HTMLElement | null>(null)

const canSubmit = computed((): boolean => {
  return !isLoading.value && !isNonEmptyString(error.value) && formEl.value != null
})

const showForm = computed((): boolean => {
  return formHTML.value != null
})

const showLoadingIndicator = (show: boolean): void => {
  isLoading.value = show
}

const removeForm = (): void => {
  formEl.value = null
  formHTML.value = null
}

const attachToFormEvents = (): void => {
  formEl.value = modal.value?.modalEl?.querySelector(':scope form[ref="inputForm"]') ?? null

  if (formEl.value && formEl.value.getAttribute('data-form-initialized') !== 'true') {
    // need to set initialized attribute, so attachment to events
    // does not happen multiple times if formHTML does not changes
    // between the requests
    formEl.value.setAttribute('data-form-initialized', 'true')

    // force request to be json
    formEl.value.setAttribute('data-type', 'json')

    // initialize form
    initializeFormFields(formEl.value)

    // attach to events
    formEl.value.addEventListener('ajax:beforeSend', () => {
      showLoadingIndicator(true)
      return true
    })

    formEl.value.addEventListener('ajax:success', (e) => {
      const [data] = (e as CustomEvent<[FormResponse]>).detail
      setupForm(data)
      showLoadingIndicator(false)
    })

    formEl.value.addEventListener('ajax:error', (e) => {
      const [, status, xhr] = (e as CustomEvent<[unknown, string, XMLHttpRequest]>).detail
      alert(`Error: ${xhr.status} - ${status}`)
      showLoadingIndicator(false)
    })
  }
}

const setupForm = (responseData: FormResponse): void => {
  removeForm()

  const status = responseData.status
  if (typeof status !== 'undefined' && status != null) {
    if (status === 'success') {
      // form operation succeeded
      emit('success')
      return
    }
  }

  const html = responseData.html ?? null

  // to ensure that form html is updated correctly
  // perform form html manipulation on nextTick
  // because html has just been unset at the begining
  // of this method and vue has not had time to celar form
  nextTick(() => {
    formHTML.value = html
    if (formHTML.value) {
      nextTick(() => {
        attachToFormEvents()
      })
    }
  })
}

const loadForm = (formUrl: string): void => {
  error.value = null
  removeForm()

  showLoadingIndicator(true)

  axios.get<FormResponse>(formUrl)
    .then((response) => {
      showLoadingIndicator(false)
      setupForm(response.data)
    })
    .catch((requestError) => {
      error.value = requestError.message
      showLoadingIndicator(false)
    })
}

const showModal = (formUrl: string): void => {
  modal.value?.show()

  // load form's html
  loadForm(formUrl)
}

const hideModal = (): void => {
  modal.value?.hide()
}

const submitForm = (): void => {
  if (formEl.value) {
    Rails.fire(formEl.value, 'submit')
  } else {
    alert('Form not initialized')
  }
}

const handleRemoveFromEventClick = (e: JQuery.ClickEvent): void => {
  if (confirm('Are you sure?')) {
    const $rmButton = $(e.target)
    const $row = $rmButton.closest('tr')
    const $destroyField = $row.find('[ref=_destroy]')
    if ($destroyField.val() === '1') {
      $row.removeClass('removed')
      $destroyField.val('')
      $rmButton.text($rmButton.data('remove-title'))
      $rmButton.removeClass('btn-info')
      $rmButton.addClass('btn-danger')
    } else {
      $row.addClass('removed')
      $destroyField.val('1')
      $rmButton.text($rmButton.data('restore-title'))
      $rmButton.removeClass('btn-danger')
      $rmButton.addClass('btn-info')
    }
  }
}

onMounted(() => {
  const modalEl = modal.value?.modalEl
  if (modalEl) {
    $(modalEl).on('click', '[data-action=remove-from-event]', handleRemoveFromEventClick)
  }
})

onBeforeUnmount(() => {
  const modalEl = modal.value?.modalEl
  if (modalEl) {
    $(modalEl).off('click', '[data-action=remove-from-event]', handleRemoveFromEventClick)
  }
})

defineExpose({
  hideModal,
  showModal,
})
</script>
